import { cn } from '@repo/lib/cn';
import { useRef } from 'react';

import type { GradeBand } from './gradeScale.types';

import { getBandSpan } from './gradeScaleBands';
import { gradeScaleColorAt, gradeScalePalette } from './gradeScaleColors';

export interface GradeBandBarProps {
  bands: GradeBand[];
  /** Draws a vertical marker with its value at this percent along the bar. */
  markPercent?: number;
  /** When set, the marker becomes draggable and reports the picked percent. */
  onMarkChange?: (percent: number) => void;
  className?: string;
}

/**
 * Horizontal colored bar of grade bands: each segment is sized by its span and
 * labelled with its name and percentage range. Self-contained so it can be
 * reused wherever a grade scale needs a compact visual summary; pass
 * `onMarkChange` to let the user drag the marker to pick a percentage.
 */
export function GradeBandBar(gradeBandBarProps: GradeBandBarProps) {
  const { bands, markPercent, onMarkChange, className } = gradeBandBarProps;
  const palette: string[] = gradeScalePalette(bands.length);
  const barRef = useRef<HTMLDivElement>(null);

  const total: number = bands.at(-1)?.maxPercent ?? 100;
  const markLeft: number | null = markPercent == null ? null : Math.min(Math.max((markPercent / total) * 100, 0), 100);

  const draggable = onMarkChange != null;
  const mark = markPercent ?? 0;

  const percentFromClientX = (clientX: number): number => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.round(Math.min(Math.max(ratio, 0), 1) * total);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!onMarkChange) return;
    event.preventDefault();
    onMarkChange(percentFromClientX(event.clientX));
  };

  return (
    <div className={cn('relative w-full', markLeft !== null && 'pt-5', className)}>
      <div
        ref={barRef}
        onPointerDown={draggable ? handleTrackPointerDown : undefined}
        className={cn('flex h-12 w-full items-stretch overflow-hidden rounded-md', draggable && 'cursor-pointer touch-none select-none')}
      >
        {bands.map((band, index) => {
          const span: number = getBandSpan(bands, index);
          const color: string = palette[index] ?? gradeScaleColorAt(0);
          return (
            <div
              key={`${band.name}-${index}`}
              className="flex min-w-0 flex-col items-center justify-center px-1 text-center"
              style={{
                flexGrow: Math.max(span, 1),
                flexBasis: 0,
                backgroundColor: color,
                color: `contrast-color(${color})`,
              }}
            >
              <span className="truncate text-xs font-semibold">{band.name}</span>
              <span className="truncate text-[0.625rem] opacity-80">
                {band.minPercent}-{band.maxPercent}%
              </span>
            </div>
          );
        })}
      </div>

      {markLeft !== null &&
        (draggable ? (
          <button
            type="button"
            role="slider"
            aria-label="Wynik procentowy"
            aria-orientation="horizontal"
            aria-valuenow={mark}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuetext={`${mark}%`}
            className="outline-focus-ring group absolute top-5 bottom-0 z-10 grid w-7 -translate-x-1/2 cursor-ew-resize touch-none place-items-center rounded-md outline-0 focus-visible:outline-3"
            style={{ left: `${markLeft}%` }}
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
                return;
              }
              onMarkChange?.(percentFromClientX(event.clientX));
            }}
            onKeyDown={(event) => {
              const step: number = event.shiftKey ? 10 : 1;
              const next: number | undefined = {
                ArrowLeft: mark - step,
                ArrowDown: mark - step,
                ArrowRight: mark + step,
                ArrowUp: mark + step,
                Home: 0,
                End: total,
              }[event.key];
              if (next === undefined) return;
              event.preventDefault();
              onMarkChange?.(Math.min(Math.max(next, 0), total));
            }}
          >
            <span className="bg-gray text-gray-foreground absolute -top-5 left-1/2 -translate-x-1/2 rounded-md px-1.5 py-0.5 text-[0.625rem] leading-none font-semibold shadow-sm transition-transform group-hover:scale-110 group-active:scale-110">
              {mark}%
            </span>
            <span aria-hidden className="bg-gray absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded-full transition-[width] group-hover:w-1 group-focus-visible:w-1 group-active:w-1" />
          </button>
        ) : (
          <>
            <span
              className="bg-gray text-gray-foreground pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-md px-1.5 py-0.5 text-[0.625rem] leading-none font-semibold shadow-sm"
              style={{ left: `${markLeft}%` }}
            >
              {mark}%
            </span>
            <span className="bg-gray pointer-events-none absolute top-5 bottom-0 z-10 w-0.5 -translate-x-1/2 rounded-full" style={{ left: `${markLeft}%` }} />
          </>
        ))}
    </div>
  );
}
